import { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageProvider";
import { useAssumptions, useCreateAccount, useDashboard } from "../hooks/api";
import { OnTrackHeadline } from "../components/OnTrackHeadline";
import { ProjectionChart } from "../components/charts";
import { AccountForm } from "../components/AccountForm";
import { Button, Panel, StatePanel } from "../components/primitives";
import { formatMoney } from "../lib/format";

export function DashboardScreen() {
  const { t, locale } = useLanguage();
  const dash = useDashboard();
  const assumptions = useAssumptions(); // parallel — for the real plan-to-age in the headline label
  const createAccount = useCreateAccount();
  const [addOpen, setAddOpen] = useState(false);

  if (dash.isLoading) return <main id="main-content" className="app-main"><StatePanel kind="loading" /></main>;
  if (dash.isError || !dash.data) return <main id="main-content" className="app-main"><StatePanel kind="error" onRetry={() => dash.refetch()} /></main>;

  const d = dash.data;
  const currency = d.netWorth.currency;
  const empty = d.accounts.length === 0;
  const planToAge = assumptions.data?.planToAge ?? 90;

  return (
    <main id="main-content" className="app-main">
      <OnTrackHeadline
        onTrack={d.onTrack}
        probability={d.probabilityOfSuccess}
        projectedRetirementAge={d.projectedRetirementAge}
        planToAge={planToAge}
      />
      <p className="tabular" style={{ fontSize: "var(--font-size-h2)" }}>
        {t("netWorth")}: {formatMoney(d.netWorth.amountMinorUnits, currency, locale)}
      </p>

      <div className="dashboard-grid">
        <Panel title={t("accounts")}>
          {empty ? (
            <StatePanel kind="empty" message="Add your first account to see your net worth.">
              <Button variant="primary" onClick={() => setAddOpen(true)}>{t("addAccount")}</Button>
            </StatePanel>
          ) : (
            <>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {d.accounts.map((a) => (
                  <li key={a.accountId} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--color-border-subtle)" }}>
                    <span>{a.name}</span>
                    <span className="tabular">{formatMoney(a.value.amountMinorUnits, a.value.currency, locale)}</span>
                  </li>
                ))}
              </ul>
              <Button variant="secondary" onClick={() => setAddOpen(true)} style={{ marginTop: 16 }}>
                + {t("addAccount")}
              </Button>
            </>
          )}
        </Panel>

        <Panel title={`${t("projection")} (${t("real")})`}>
          {d.projectionSummary && d.projectionSummary.length > 0 ? (
            <ProjectionChart series={d.projectionSummary} label={t("projection")} />
          ) : (
            <StatePanel kind="empty" message="Set your assumptions to see the projection." />
          )}
        </Panel>
      </div>

      <nav className="tab-row" aria-label="Sections">
        <Link className="btn btn--secondary" to="/projection">{t("projection")}</Link>
        <Link className="btn btn--secondary" to="/montecarlo">{t("montecarlo")}</Link>
        <Link className="btn btn--secondary" to="/assumptions">{t("assumptions")}</Link>
      </nav>

      <AccountForm
        open={addOpen}
        mode="add"
        currency={currency}
        saving={createAccount.isPending}
        onClose={() => setAddOpen(false)}
        onSave={(input) => createAccount.mutate(input, { onSuccess: () => setAddOpen(false) })}
      />
    </main>
  );
}
